/**
 * Marketplace Landing Page - Todo en Local
 * Modern e-commerce design with gray canvas, white containers, gradient hero
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, MapPin, ShoppingBag, Store, Truck, Package, Star,
  ChevronRight, Heart, TrendingUp, Clock, Shield, Smartphone, ShoppingCart, User, LogOut,
  Home, Grid3X3, CreditCard, BadgeCheck, Facebook, Instagram, Twitter
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import api from '../../services/api';
import { getLandingLayout, getSocialLinks } from '../../services/api';
import { API_URL } from '../../config';
import SearchAutocomplete from '../../components/marketplace/SearchAutocomplete';
import { BannerCarousel, BannerRow, HeroBanner } from '../../components/marketplace/BannerSection';
import { VerifiedStoreName } from '../../components/marketplace/VerifiedBadge';
import FeaturedStores from '../../components/marketplace/FeaturedStores';
import NewStores from '../../components/marketplace/NewStores';
import OffersCarousel from '../../components/marketplace/OffersCarousel';
import PassExperiencesSection from '../../components/marketplace/PassExperiencesSection';

// Brand colors
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f3f4f6',   // gray-100 — main canvas
  mediumGray: '#64748b',
  white: '#ffffff',
  greenLight: '#1e3a5f',
  greenDark: '#0f172a',
  dark: '#0f172a'
};

const BRAND_LOGO = '/logo.png';

// Check if branch is currently open
const isBranchCurrentlyOpen = (branch) => {
  if (!branch) return true;
  if (branch.is_manually_closed) return false;
  if (branch.is_active === false) return false;
  const hours = branch.business_hours;
  if (!hours) return true;
  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[now.getDay()];
  const currentTime = now.toTimeString().slice(0, 5);
  const schedule = hours[currentDay];
  if (!schedule || !schedule.is_open) return false;
  const openTime = schedule.open_time || '09:00';
  const closeTime = schedule.close_time || '18:00';
  return currentTime >= openTime && currentTime < closeTime;
};

// Default layout
const DEFAULT_LAYOUT = [
  { section_id: 'features', label: 'Beneficios', enabled: true, order: 1 },
  { section_id: 'banner_carousel', label: 'Banners Carrusel', enabled: true, order: 2 },
  { section_id: 'categories', label: 'Categorias', enabled: true, order: 3 },
  { section_id: 'featured_stores', label: 'Tiendas Destacadas', enabled: true, order: 4 },
  { section_id: 'offers', label: 'Ofertas y Descuentos', enabled: true, order: 5 },
  { section_id: 'trending', label: 'Productos Populares', enabled: true, order: 6 },
  { section_id: 'new_stores', label: 'Nuevas Tiendas', enabled: true, order: 7 },
  { section_id: 'banner_row', label: 'Banners Fila', enabled: true, order: 8 },
  { section_id: 'nearby', label: 'Tiendas Cerca de Ti', enabled: true, order: 9 },
];

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.46a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.11z"/>
  </svg>
);

// ==================== WHITE CONTAINER WRAPPER ====================
const SectionContainer = ({ children, className = '', noPad = false }) => (
  <div className={`max-w-7xl mx-auto px-4 mt-4 ${className}`}>
    <div className={`bg-white rounded-lg shadow-sm ${noPad ? '' : 'p-5 sm:p-6'}`}>
      {children}
    </div>
  </div>
);

const MarketplaceLanding = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [selectedMasterCat, setSelectedMasterCat] = useState(null);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('customer_token'));
  const [favorites, setFavorites] = useState(new Set());
  const [cartInfo, setCartInfo] = useState({ itemCount: 0, total: 0 });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [banners, setBanners] = useState({ hero: [], carousel: [], row: [] });
  const [socialLinks, setSocialLinks] = useState({ facebook: null, instagram: null, twitter: null, tiktok: null });
  const [cityName, setCityName] = useState(null);
  const userMenuRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    setIsLoggedIn(false);
    setShowUserMenu(false);
    navigate('/');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadInitialData();
    getUserLocation();
    loadFavorites();
    loadCartInfo();
    loadLayout();
    loadBanners();
    getSocialLinks().then(res => setSocialLinks(res.data)).catch(() => {});
    setIsLoggedIn(!!localStorage.getItem('customer_token'));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLayout = async () => {
    try {
      const res = await getLandingLayout();
      if (Array.isArray(res.data) && res.data.length > 0) setLayout(res.data);
    } catch (e) {}
  };

  const loadBanners = async () => {
    try {
      const response = await api.get('/banners/public');
      setBanners(response.data);
    } catch (error) {
      console.error('Error loading banners:', error);
    }
  };

  const loadCartInfo = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) return;
    try {
      const response = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      setCartInfo({ itemCount: response.data.items?.length || 0, total: response.data.total || 0 });
    } catch (error) {}
  };

  const loadFavorites = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) return;
    try {
      const response = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
      const favIds = new Set(response.data.map(f => f.product_id));
      setFavorites(favIds);
    } catch (error) {}
  };

  const toggleFavorite = async (e, productId, businessId = null) => {
    e.preventDefault();
    e.stopPropagation();
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) {
      toast.info('Inicia sesion para guardar favoritos');
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    try {
      if (favorites.has(productId)) {
        const favResponse = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
        const favToRemove = favResponse.data.find(f => (f.product?.id || f.product_id) === productId);
        if (favToRemove) {
          await api.delete(`/marketplace/favorites/${favToRemove.favorite_id}?customer_token=${customerToken}`);
          setFavorites(prev => { const next = new Set(prev); next.delete(productId); return next; });
          toast.success('Eliminado de favoritos');
        }
      } else {
        await api.post(`/marketplace/favorites?customer_token=${customerToken}`, { product_id: productId, business_id: businessId });
        setFavorites(prev => new Set([...prev, productId]));
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const reverseGeocode = async (lat, lng) => {
    // Check cached city
    const cached = localStorage.getItem('userCityName');
    if (cached) { setCityName(cached); return; }
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`);
      const data = await res.json();
      const city = data.address?.city || data.address?.town || data.address?.municipality || data.address?.state || null;
      if (city) {
        setCityName(city);
        localStorage.setItem('userCityName', city);
      }
    } catch (e) {}
  };

  const getUserLocation = () => {
    const cachedLocation = localStorage.getItem('userLocation');
    if (cachedLocation) {
      try {
        const loc = JSON.parse(cachedLocation);
        const cacheTime = localStorage.getItem('userLocationTime');
        if (cacheTime && Date.now() - parseInt(cacheTime) < 300000) {
          setUserLocation(loc);
          reverseGeocode(loc.lat, loc.lng);
          return;
        }
      } catch (e) {}
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserLocation(loc);
          localStorage.setItem('userLocation', JSON.stringify(loc));
          localStorage.setItem('userLocationTime', Date.now().toString());
          reverseGeocode(loc.lat, loc.lng);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 300000 }
      );
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [catResponse, mcResponse] = await Promise.all([
        api.get('/marketplace/categories'),
        api.get('/master-categories/public'),
      ]);
      setCategories(catResponse.data);
      setMasterCategories(mcResponse.data);
      if (mcResponse.data.length > 0) setSelectedMasterCat(mcResponse.data[0].id);
      const locationParams = userLocation ? `&lat=${userLocation.lat}&lng=${userLocation.lng}` : '';
      const trendingResponse = await api.get(`/marketplace/search?limit=8&sort_by=relevance${locationParams}`);
      setTrendingProducts(trendingResponse.data.products || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userLocation) loadNearbyStores(); }, [userLocation]);

  const loadNearbyStores = async () => {
    if (!userLocation) return;
    try {
      const response = await api.get(`/marketplace/nearby?lat=${userLocation.lat}&lng=${userLocation.lng}&limit=6`);
      setNearbyStores(response.data);
    } catch (error) {}
  };

  // ==================== SECTION COMPONENTS ====================

  // Compact features bar
  const FeaturesSection = () => (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between py-3 overflow-x-auto scrollbar-hide gap-4 sm:gap-0">
          {[
            { icon: MapPin, text: 'Tiendas locales cerca de ti', color: COLORS.navyBlue },
            { icon: Truck, text: 'Entrega a domicilio', color: COLORS.orange },
            { icon: Store, text: 'Recoge sin costo extra', color: COLORS.navyBlue },
            { icon: Shield, text: 'Compra segura', color: '#16a34a' },
          ].map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <div className="hidden sm:block w-px h-6 bg-gray-200 flex-shrink-0" />}
              <div className="flex items-center gap-2 flex-shrink-0 px-2">
                <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                <span className="text-xs sm:text-sm text-slate-600 whitespace-nowrap">{item.text}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  const BannerCarouselSection = () => {
    if (banners.carousel.length === 0) return null;
    return (
      <SectionContainer>
        <BannerCarousel banners={banners.carousel} />
      </SectionContainer>
    );
  };

  const BannerRowSection = () => {
    if (banners.row.length === 0) return null;
    return (
      <SectionContainer>
        <BannerRow banners={banners.row} />
      </SectionContainer>
    );
  };

  const CategoriesSection = () => {
    const allFiltered = selectedMasterCat
      ? categories.filter(c => c.master_category_id === selectedMasterCat)
      : categories;
    const visibleCats = allFiltered.slice(0, 8);
    const hasMore = allFiltered.length > 8;
    const selectedMcName = masterCategories.find(mc => mc.id === selectedMasterCat)?.name;

    const getCatImgSrc = (cat) => {
      const url = cat.thumbnail_url || cat.image_url;
      if (!url) return null;
      if (url.startsWith('http')) return url;
      return `${API_URL}${url}`;
    };

    if (loading && categories.length === 0) {
      return (
        <SectionContainer>
          <div className="h-7 w-32 bg-gray-200 rounded animate-pulse mb-6" />
          <div className="flex gap-3 overflow-hidden">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="flex-shrink-0 w-24 h-28 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </SectionContainer>
      );
    }
    if (categories.length === 0) return null;

    return (
      <SectionContainer>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-slate-800">Categorias</h2>
          <Link to="/categorias" className="flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: COLORS.navyBlue }}>
            Ver todas <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Master categories — horizontal scroll */}
        <div className="flex gap-2.5 overflow-x-auto p-1 -mx-1 scrollbar-hide mb-4">
          {masterCategories.map((mc) => {
            const isActive = selectedMasterCat === mc.id;
            const mcImg = mc.thumbnail_url || mc.image_url;
            const mcImgSrc = mcImg ? (mcImg.startsWith('http') ? mcImg : `${API_URL}${mcImg}`) : null;
            return (
              <button
                key={mc.id}
                onClick={() => setSelectedMasterCat(mc.id)}
                className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg transition-all flex-shrink-0 ${
                  isActive
                    ? 'bg-slate-50 ring-2 ring-offset-1'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={isActive ? { ringColor: COLORS.navyBlue, '--tw-ring-color': COLORS.navyBlue } : {}}
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                  {mcImgSrc ? (
                    <img src={mcImgSrc} alt={mc.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <span className="text-2xl sm:text-3xl">{mc.icon}</span>
                  )}
                </div>
                <span className={`text-[11px] sm:text-xs font-medium text-center leading-tight ${isActive ? 'text-slate-800' : 'text-slate-500'}`}>
                  {mc.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* Separator */}
        {selectedMcName && (
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-medium text-slate-400">Categorias en {selectedMcName}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        )}

        {/* Sub-categories — horizontal pills on mobile, grid on desktop */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5">
          {visibleCats.map((cat, idx) => (
            <Link
              key={cat.id}
              to={`/buscar?category=${cat.id}`}
              className={`flex flex-col items-center gap-1.5 p-2.5 bg-gray-50 rounded-lg hover:shadow-md hover:bg-white transition-all group ${
                idx >= 6 ? 'hidden sm:flex' : ''
              }`}
            >
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform">
                {getCatImgSrc(cat) ? (
                  <img src={getCatImgSrc(cat)} alt={cat.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <span className="text-[11px] text-center font-medium leading-tight text-slate-600">{cat.name}</span>
            </Link>
          ))}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <Link
              to={selectedMasterCat ? `/categorias?mc=${selectedMasterCat}` : '/categorias'}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 hover:shadow-sm transition-all"
              style={{ color: COLORS.navyBlue }}
            >
              Ver las {allFiltered.length} categorias <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </SectionContainer>
    );
  };

  const TrendingSkeleton = () => (
    <SectionContainer>
      <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-6" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1,2,3,4,5,6,7,8].map(i => (
          <div key={i} className="bg-white rounded-lg overflow-hidden border border-gray-100">
            <div className="aspect-square bg-gray-100 animate-pulse" />
            <div className="p-3 space-y-2">
              <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              <div className="h-5 w-1/2 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </SectionContainer>
  );

  const TrendingSection = () => {
    if (loading && trendingProducts.length === 0) return <TrendingSkeleton />;
    if (trendingProducts.length === 0) return null;

    return (
        <SectionContainer>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: COLORS.orange }} />
              <h2 className="text-lg sm:text-xl font-bold text-slate-800">Productos Populares</h2>
            </div>
            <Link to="/buscar?sort=trending" className="flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: COLORS.navyBlue }}>
              Ver mas <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {trendingProducts.slice(0, 8).map((item) => {
              const price = item.product?.price || item.price || item.retail_price || 0;
              const comparePrice = item.product?.compare_at_price || item.compare_at_price;
              const hasDiscount = comparePrice && comparePrice > price;
              const discountPct = hasDiscount ? Math.round((1 - price / comparePrice) * 100) : 0;
              const productId = item.product?.id || item.id;
              const reviewCount = item.store?.review_count || 0;

              return (
                <Link
                  key={`${productId}-${item.branch?.id || 'default'}`}
                  to={`/producto/${productId}`}
                  className="bg-white rounded-lg overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all group border border-gray-100"
                >
                  <div className="aspect-square bg-gray-50 relative overflow-hidden">
                    <img
                      src={getProductImage(item.product?.thumbnail_url || item.thumbnail_url || item.product?.image_url || item.image_url)}
                      alt={item.product?.name || item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => { e.target.src = getProductImage(); }}
                    />
                    <button
                      className={`absolute top-2 right-2 p-1.5 bg-white rounded-lg shadow transition-all hover:scale-110 ${favorites.has(productId) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      onClick={(e) => toggleFavorite(e, productId, item.store?.id || item.business_id, item.branch?.id)}
                    >
                      <Heart className={`w-4 h-4 ${favorites.has(productId) ? 'text-red-500 fill-red-500' : 'text-slate-400'}`} />
                    </button>
                    {hasDiscount && (
                      <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[11px] font-bold rounded-md bg-red-500 text-white">
                        -{discountPct}%
                      </span>
                    )}
                    {item.branch?.business_hours && (
                      <div className={`absolute bottom-2 left-2 px-2 py-0.5 rounded-md text-[11px] font-medium ${
                        isBranchCurrentlyOpen(item.branch) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${isBranchCurrentlyOpen(item.branch) ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {isBranchCurrentlyOpen(item.branch) ? 'Abierto' : 'Cerrado'}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-slate-800 line-clamp-2 text-sm leading-tight">{item.product?.name || item.name}</h3>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="text-lg font-bold" style={{ color: COLORS.navyBlue }}>
                        ${price.toFixed(2)}
                      </span>
                      {hasDiscount && (
                        <span className="text-xs text-slate-400 line-through">
                          ${comparePrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {hasDiscount && (
                      <span className="text-xs font-medium text-green-600">
                        Ahorras ${(comparePrice - price).toFixed(2)}
                      </span>
                    )}
                    {item.store?.rating > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3 h-3 ${s <= Math.round(item.store.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200 fill-slate-200'}`} />
                          ))}
                        </div>
                        <span className="text-[11px] text-slate-500">
                          {item.store.rating.toFixed(1)}
                          {reviewCount > 0 && ` (${reviewCount})`}
                        </span>
                      </div>
                    )}
                    {item.store && (
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
                        <Store className="w-3 h-3 flex-shrink-0" />
                        <VerifiedStoreName
                          name={item.store.name}
                          verified={item.store.verified}
                          size="xs"
                          nameClassName="truncate"
                        />
                      </div>
                    )}
                    {item.branch?.distance_km !== undefined && item.branch?.distance_km !== null && (
                      <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                        <MapPin className="w-3 h-3" />
                        <span>{item.branch.distance_km} km</span>
                      </div>
                    )}
                    {/* Delivery badges */}
                    <div className="flex flex-wrap items-center gap-1 mt-2">
                      {item.store?.delivery_enabled && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                          <Truck className="w-2.5 h-2.5" /> Envio
                        </span>
                      )}
                      {item.store?.pickup_enabled && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                          <Package className="w-2.5 h-2.5" /> Recoge
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </SectionContainer>
    );
  };

  const NearbySkeleton = () => (
    <SectionContainer>
      <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[1,2,3].map(i => (
          <div key={i} className="bg-gray-50 rounded-lg p-4 flex items-start gap-3">
            <div className="w-14 h-14 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </SectionContainer>
  );

  const NearbySection = () => {
    if (userLocation && nearbyStores.length === 0 && loading) return <NearbySkeleton />;
    if (nearbyStores.length === 0) return null;
    return (
        <SectionContainer>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
              <h2 className="text-lg sm:text-xl font-bold text-slate-800">Tiendas Cerca de Ti</h2>
            </div>
            <Link to="/buscar?view=map" className="flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: COLORS.navyBlue }}>
              Ver mapa <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {nearbyStores.slice(0, 6).map((item, idx) => (
              <Link
                key={idx}
                to={`/tienda/${item.store.id}`}
                className="bg-gray-50 rounded-lg p-4 hover:shadow-md hover:bg-white transition-all flex items-start gap-3"
              >
                <div className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 bg-white overflow-hidden">
                  {item.store.logo_url ? (
                    <img src={item.store.logo_url} alt={item.store.name} className="w-full h-full object-cover rounded-lg" loading="lazy" />
                  ) : (
                    <Store className="w-7 h-7" style={{ color: COLORS.navyBlue }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <VerifiedStoreName
                    name={item.store.name}
                    verified={item.store.verified}
                    size="sm"
                    nameClassName="font-semibold truncate"
                    className="truncate"
                  />
                  <p className="text-sm text-slate-500 truncate">{item.branch.address}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <MapPin className="w-3 h-3" />
                      {item.branch.distance_km} km
                    </span>
                    {item.store.delivery_enabled && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                        <Truck className="w-2.5 h-2.5" /> Envio
                      </span>
                    )}
                    {item.store.pickup_enabled && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                        <Package className="w-2.5 h-2.5" /> Recoge
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </SectionContainer>
    );
  };

  // Section map
  const SECTION_MAP = {
    features: FeaturesSection,
    banner_carousel: BannerCarouselSection,
    categories: CategoriesSection,
    featured_stores: FeaturedStores,
    offers: OffersCarousel,
    trending: TrendingSection,
    new_stores: NewStores,
    banner_row: BannerRowSection,
    nearby: NearbySection,
  };

  const activeSections = layout.filter(s => s.enabled).sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ====== HEADER ====== */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: COLORS.darkSlate }}>
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between gap-3">
            {/* Logo + Location */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img src={BRAND_LOGO} alt="Todo en Local" className="h-9 sm:h-11 w-auto brightness-0 invert" />
              </Link>
              {/* City indicator — desktop */}
              <div className="hidden md:flex items-center gap-1.5 text-white/70 hover:text-white transition-colors cursor-default pl-3 border-l border-white/10">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-xs leading-tight">
                  {cityName ? (
                    <><span className="text-white/50 block text-[10px]">Enviar a</span><span className="font-medium text-white text-xs">{cityName}</span></>
                  ) : (
                    <span className="text-white/50">Tu ubicacion</span>
                  )}
                </span>
              </div>
            </div>

            {/* Search — wide and prominent on dark bg */}
            <div className="flex-1 max-w-2xl mx-2 sm:mx-5">
              <SearchAutocomplete
                placeholder="Buscar productos, tiendas..."
                className="w-full"
                variant="header"
              />
            </div>

            {/* Actions — white icons on dark */}
            <div className="flex items-center gap-1 sm:gap-1.5">
              <Link to="/favoritos" className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors">
                <Heart className="w-5 h-5 text-white" />
                <span className="text-[10px] text-white/60 mt-0.5">Favoritos</span>
              </Link>
              <Link to="/carrito" className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors relative">
                <ShoppingBag className="w-5 h-5 text-white" />
                <span className="text-[10px] text-white/60 mt-0.5">Carrito</span>
                {cartInfo.itemCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full text-white text-[10px] flex items-center justify-center font-bold" style={{ backgroundColor: COLORS.orange }}>
                    {cartInfo.itemCount > 9 ? '9+' : cartInfo.itemCount}
                  </span>
                )}
              </Link>

              {/* Mobile user menu */}
              <div className="relative sm:hidden" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="p-2 rounded-lg transition-colors hover:bg-white/10"
                >
                  <User className="w-5 h-5 text-white" />
                  {cartInfo.itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center font-bold" style={{ backgroundColor: COLORS.orange }}>
                      {cartInfo.itemCount}
                    </span>
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                    {cityName && (
                      <>
                        <div className="flex items-center gap-2 px-4 py-2.5 text-slate-500">
                          <MapPin className="w-4 h-4" />
                          <span className="text-xs">{cityName}</span>
                        </div>
                        <div className="border-t border-gray-100 my-1" />
                      </>
                    )}
                    {isLoggedIn ? (
                      <Link to="/mi-cuenta" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                        <User className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                        <span className="font-medium text-slate-700">Mi Cuenta</span>
                      </Link>
                    ) : (
                      <Link to="/auth" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                        <User className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                        <span className="font-medium text-slate-700">Ingresar</span>
                      </Link>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <Link to="/carrito" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                      <ShoppingBag className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                      <span className="font-medium text-slate-700">Carrito</span>
                      {cartInfo.itemCount > 0 && (
                        <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: COLORS.orange }}>{cartInfo.itemCount}</span>
                      )}
                    </Link>
                    <Link to="/favoritos" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                      <Heart className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                      <span className="font-medium text-slate-700">Favoritos</span>
                    </Link>
                    {isLoggedIn && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors w-full text-left">
                          <LogOut className="w-5 h-5 text-red-500" />
                          <span className="font-medium text-red-600">Cerrar sesion</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop auth */}
              {isLoggedIn ? (
                <Link
                  to="/mi-cuenta"
                  className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <User className="w-5 h-5 text-white" />
                  <span className="text-[10px] text-white/60 mt-0.5">Mi Cuenta</span>
                </Link>
              ) : (
                <Link
                  to="/auth"
                  className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <User className="w-5 h-5 text-white" />
                  <span className="text-[10px] text-white/60 mt-0.5">Ingresar</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ====== HERO BANNER with gradient fade ====== */}
      <HeroBanner banners={banners.hero}>
        <div className="max-w-2xl">
          {/* Title & subtitle only when no hero banners (dark fallback) */}
          {banners.hero.length === 0 && (
            <>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
                Encuentra todo lo que necesitas <span style={{ color: COLORS.orange }}>cerca de ti</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 mb-6">
                Descubre productos de tiendas locales, compara precios y recibe en tu domicilio.
              </p>
            </>
          )}
          <SearchAutocomplete placeholder="¿Que estas buscando?" size="large" className="w-full" />
          {userLocation && banners.hero.length === 0 && (
            <div className="flex items-center gap-2 mt-3 text-slate-400">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Mostrando resultados cerca de tu ubicacion</span>
            </div>
          )}
        </div>
      </HeroBanner>

      {/* ====== DYNAMIC SECTIONS ====== */}
      {activeSections.map(section => {
        const Component = SECTION_MAP[section.section_id];
        if (!Component) return null;
        return <Component key={section.section_id} />;
      })}

      {/* ====== PASS EXPERIENCES ====== */}
      <PassExperiencesSection limit={6} />

      {/* ====== TRUST / PAYMENT BAR ====== */}
      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Compra Protegida</p>
                <p className="text-xs text-slate-500">Tu pago seguro en cada compra</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 font-medium">Metodos de pago:</span>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-blue-600 text-white text-[10px] font-bold tracking-wider">VISA</span>
                <span className="px-2 py-1 rounded-md bg-red-500 text-white text-[10px] font-bold tracking-wider">MC</span>
                <span className="px-2 py-1 rounded-md bg-slate-700 text-white text-[10px] font-bold tracking-wider">SPEI</span>
                <span className="px-2 py-1 rounded-md bg-green-600 text-white text-[10px] font-bold tracking-wider flex items-center gap-1">
                  <CreditCard className="w-3 h-3" /> Efectivo
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <BadgeCheck className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Tiendas Verificadas</p>
                <p className="text-xs text-slate-500">Negocios reales de tu comunidad</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== CTA SECTION ====== */}
      <div className="mt-6">
        <section className="py-14" style={{ backgroundColor: COLORS.darkSlate }}>
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Tienes un negocio local?</h2>
            <p className="text-slate-400 mb-7 max-w-2xl mx-auto text-sm sm:text-base">
              Unete a Todo en Local y lleva tus productos a miles de clientes en tu zona.
              Gestiona tu inventario, recibe pedidos y haz crecer tu negocio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                className="px-7 py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: COLORS.orange, color: 'white' }}
              >
                Registrar mi Tienda
              </Link>
              <Link
                to="/como-funciona"
                className="px-7 py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90"
                style={{ backgroundColor: 'white', color: COLORS.darkSlate }}
              >
                Conocer mas
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* ====== FOOTER ====== */}
      <footer style={{ backgroundColor: '#0a0f1a' }} className="text-slate-400 py-10 pb-28 sm:pb-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <img src={BRAND_LOGO} alt="Todo en Local" className="h-8 w-auto brightness-0 invert mb-3" />
              <p className="text-sm mb-4">La plataforma que conecta comercios locales con sus comunidades.</p>
              {(() => {
                const items = [
                  { key: 'facebook', url: socialLinks.facebook, icon: Facebook },
                  { key: 'instagram', url: socialLinks.instagram, icon: Instagram },
                  { key: 'twitter', url: socialLinks.twitter, icon: Twitter },
                  { key: 'tiktok', url: socialLinks.tiktok, icon: TikTokIcon },
                ].filter(s => s.url);
                return items.length > 0 ? (
                  <div className="flex items-center gap-2.5">
                    {items.map(({ key, url, icon: Icon }) => (
                      <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors">
                        <Icon className="w-4 h-4 text-slate-300" />
                      </a>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Comprar</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/buscar" className="hover:text-white transition-colors">Buscar Productos</Link></li>
                <li><Link to="/categorias" className="hover:text-white transition-colors">Categorias</Link></li>
                <li><Link to="/buscar?sort=discount" className="hover:text-white transition-colors">Ofertas</Link></li>
                <li><Link to="/tiendas" className="hover:text-white transition-colors">Tiendas</Link></li>
                <li><Link to="/experiencias" className="hover:text-white transition-colors">Experiencias Pass</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Para Negocios</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register" className="hover:text-white transition-colors">Registrar Tienda</Link></li>
                <li><Link to="/como-funciona" className="hover:text-white transition-colors">Como Funciona</Link></li>
                <li><Link to="/precios" className="hover:text-white transition-colors">Precios</Link></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Informacion</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/acerca" className="hover:text-white transition-colors">Acerca de Nosotros</Link></li>
                <li><Link to="/contacto" className="hover:text-white transition-colors">Contacto</Link></li>
                <li><Link to="/privacidad" className="hover:text-white transition-colors">Politica de Privacidad</Link></li>
                <li><Link to="/terminos" className="hover:text-white transition-colors">Terminos y Condiciones</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">Preguntas Frecuentes</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} Todo en Local. Todos los derechos reservados.</p>
            <div className="flex items-center gap-4 text-xs">
              <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
              <Link to="/terminos" className="hover:text-white transition-colors">Terminos</Link>
              <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ====== MOBILE BOTTOM NAV ====== */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 sm:hidden">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-0.5 px-3 py-1">
            <Home className="w-5 h-5" style={{ color: COLORS.orange }} />
            <span className="text-[10px] font-medium" style={{ color: COLORS.orange }}>Inicio</span>
          </Link>
          <Link to="/categorias" className="flex flex-col items-center gap-0.5 px-3 py-1">
            <Grid3X3 className="w-5 h-5 text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500">Categorias</span>
          </Link>
          <Link to="/carrito" className="flex flex-col items-center gap-0.5 px-3 py-1 relative">
            <ShoppingCart className="w-5 h-5 text-slate-500" />
            {cartInfo.itemCount > 0 && (
              <span className="absolute -top-0.5 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                {cartInfo.itemCount > 9 ? '9+' : cartInfo.itemCount}
              </span>
            )}
            <span className="text-[10px] font-medium text-slate-500">Carrito</span>
          </Link>
          <Link to="/favoritos" className="flex flex-col items-center gap-0.5 px-3 py-1">
            <Heart className="w-5 h-5 text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500">Favoritos</span>
          </Link>
          <Link to={isLoggedIn ? '/mi-cuenta' : '/auth'} className="flex flex-col items-center gap-0.5 px-3 py-1">
            <User className="w-5 h-5 text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500">{isLoggedIn ? 'Cuenta' : 'Ingresar'}</span>
          </Link>
        </div>
      </nav>

      {/* Floating Cart Button — desktop */}
      {cartInfo.itemCount > 0 && (
        <button
          onClick={() => navigate('/carrito')}
          className="fixed bottom-6 right-6 hidden sm:flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white font-medium z-40 hover:scale-105 transition-transform"
          style={{ backgroundColor: COLORS.orange }}
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="bg-white text-orange-500 px-2 py-0.5 rounded-md text-sm font-bold">
            {cartInfo.itemCount}
          </span>
          <span>${cartInfo.total.toFixed(2)}</span>
        </button>
      )}
    </div>
  );
};

export default MarketplaceLanding;
